/**
 * theme.ts — Shared Light-Theme Konstanten
 *
 * Helles, zurückhaltendes Sidebar-Theme als Kontrast zum dunklen 3D-Canvas.
 * Designprinzipien:
 * - Weiße Flächen mit feinen Grauwerten für Hierarchie
 * - Zurückhaltende Typografie, klare Leserichtung
 * - Dezente Schatten statt harter Kanten
 * - Blaue Akzentfarbe (#3b82f6) für interaktive Elemente
 * - Konsistente 0.25em-Spacing-Basis (= 4px at 16px root)
 */

import React from 'react'

// ── Farben ──────────────────────────────────────────────────────────────────

export const C = {
  // Oberflächen — 3-stufige Helligkeitshierarchie
  panelBg:       '#ffffff',
  panelBgAlpha:  'rgba(255, 255, 255, 0.97)',
  pillBg:        '#f3f4f6',        // gray-100
  elevatedBg:    '#f9fafb',        // gray-50
  inputBg:       '#f5f5f7',
  inputBgHover:  '#eeeff1',
  pageBg:        '#ffffff',

  // Text — 4-stufige Kontrasthierarchie
  textPrimary:   '#111827',        // gray-900
  textSecondary: '#374151',        // gray-700
  textMuted:     '#6b7280',        // gray-500
  textDim:       '#9ca3af',        // gray-400
  textInverse:   '#ffffff',

  // Borders — 2 Stufen
  border:        '#e5e7eb',        // gray-200
  borderStrong:  '#d1d5db',        // gray-300
  borderSubtle:  '#f0f0f0',        // fast unsichtbar

  // Active / Selected
  activeBg:      'rgba(59, 130, 246, 0.08)',
  activeText:    '#2563eb',        // blue-600
  activeBorder:  'rgba(59, 130, 246, 0.3)',
  hoverBg:       'rgba(0, 0, 0, 0.03)',

  // Brand / Akzent
  primary:       '#3b82f6',        // blue-500
  primaryLight:  'rgba(59, 130, 246, 0.08)',
  primaryBorder: 'rgba(59, 130, 246, 0.22)',
  primaryText:   '#2563eb',        // blue-600

  // Destructive
  danger:        '#dc2626',        // red-600
  dangerLight:   'rgba(220, 38, 38, 0.06)',
  dangerBorder:  'rgba(220, 38, 38, 0.18)',

  // Success
  successText:   '#16a34a',        // green-600
  successLight:  'rgba(22, 163, 74, 0.06)',
  successBorder: 'rgba(22, 163, 74, 0.18)',

  // Shadows — weich, dezent
  shadowSm:      '0 0.0625em 0.125em rgba(0, 0, 0, 0.05)',
  shadowMd:      '0 0.25em 0.75em rgba(0, 0, 0, 0.08), 0 0.0625em 0.1875em rgba(0, 0, 0, 0.04)',
  shadowLg:      '0 0.625em 2em rgba(0, 0, 0, 0.10), 0 0.125em 0.5em rgba(0, 0, 0, 0.05)',

  // Radius — em-Werte (skalieren mit der font-size des Elements)
  radiusSm:      '0.25em',   // ≈ 4px at 16px
  radius:        '0.375em',  // ≈ 6px at 16px
  radiusMd:      '0.5em',    // ≈ 8px at 16px
  radiusLg:      '0.625em',  // ≈ 10px at 16px
  radiusPill:    '62.4375em',// ≈ 999px (Pill-Hack, immer groß genug)

  // Font
  fontSans:      "'PP Mori', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontMono:      "'Sohne Mono', 'SF Mono', 'Fira Code', 'JetBrains Mono', monospace",

  // Transition
  transition:    '0.15s ease',
} as const

// ── Wiederverwendbare Style-Objekte ─────────────────────────────────────────

/** Panel-Hintergrund (Sidebar, Editor, Dialog) */
export const panelStyle: React.CSSProperties = {
  background: C.panelBgAlpha,
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: `1px solid ${C.border}`,
  boxShadow: C.shadowMd,
  fontFamily: C.fontSans,
  color: C.textPrimary,
}

/** Section-Label (uppercase, muted) – z.B. "TYP", "TITEL", "TAGS" */
export const sectionLabelStyle: React.CSSProperties = {
  fontSize: '0.625em',      // 10px at 16px parent
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: C.textMuted,
  marginBottom: '0.5em',   // 5px at own 10px font-size
  fontFamily: C.fontSans,
}

/** Standard-Input (Text, Number) */
export const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '0.5em 0.6667em',  // 6px/12px, 8px/12px – relative zur eigenen 12px-Schriftgröße
  borderRadius: C.radiusSm,
  border: `1px solid ${C.border}`,
  background: C.inputBg,
  color: C.textPrimary,
  fontSize: '0.75em',          // 12px at 16px parent
  fontFamily: C.fontSans,
  outline: 'none',
  marginBottom: '1em',         // 12px at own 12px font-size
  transition: `border-color ${C.transition}`,
}

/** Primary-Button (blau) */
export const primaryBtnStyle: React.CSSProperties = {
  padding: '0.3636em 1.0909em', // 4px/11px, 12px/11px
  borderRadius: C.radiusSm,
  border: `1px solid ${C.primaryBorder}`,
  background: C.primaryLight,
  color: C.primaryText,
  fontSize: '0.6875em',         // 11px at 16px parent
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: C.fontSans,
  transition: `all ${C.transition}`,
}

/** Ghost-Button (outline) */
export const secondaryBtnStyle: React.CSSProperties = {
  padding: '0.3636em 0.9091em', // 4px/11px, 10px/11px
  borderRadius: C.radiusSm,
  border: `1px solid ${C.border}`,
  background: 'transparent',
  color: C.textSecondary,
  fontSize: '0.6875em',         // 11px at 16px parent
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: C.fontSans,
  transition: `all ${C.transition}`,
}

/** Icon-Button (nur Icon, kein Hintergrund) */
export const iconBtnStyle: React.CSSProperties = {
  padding: '0.3077em 0.6154em', // 4px/13px, 8px/13px
  borderRadius: C.radiusSm,
  border: 'none',
  background: 'none',
  color: C.textMuted,
  fontSize: '0.8125em',         // 13px at 16px parent
  cursor: 'pointer',
  lineHeight: 1,
}

/** Danger-Button (rot, outline) */
export const dangerBtnStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5em 0',           // 6px at own 12px font-size
  borderRadius: C.radius,
  border: `1px solid ${C.dangerBorder}`,
  background: C.dangerLight,
  color: C.danger,
  fontSize: '0.75em',           // 12px at 16px parent
  fontWeight: 500,
  cursor: 'pointer',
  transition: `all ${C.transition}`,
  fontFamily: C.fontSans,
}

/** Tag-Chip */
export const tagStyle: React.CSSProperties = {
  fontSize: '0.5625em',         // 9px at 16px parent
  padding: '0.1111em 0.5556em', // 1px/9px, 5px/9px
  borderRadius: '0.3333em',     // 3px at own 9px font-size
  background: C.pillBg,
  color: C.textDim,
  fontFamily: C.fontSans,
}

/** Separator-Linie (horizontal) */
export const borderBottom: React.CSSProperties = {
  borderBottom: `1px solid ${C.border}`,
}
